import { useCallback, useRef, useState } from "react";
import { Button } from "../components/Button";
import { Callout } from "../components/Callout";
import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { ApiError } from "../api/client";
import { messageForMentionError } from "../lib/mention-messages";
import { formatDisplayDateLong, formatInstantDate, formatMoney } from "../lib/format";
import styles from "./MentionsPanel.module.css";
import type {
  HonorableMentionOffer,
  PurchasedMentionResult,
  SellerDeal,
} from "../api/types";

/**
 * The Honorable Mention — canon § 10's one-day listing outside the Top 10 (canon APP-004).
 *
 * **This panel is deliberately not a board.** The auction panel above it is one: brass rank
 * marks, a countdown masthead, a column of ranked rows. There is no rank mark here and no
 * position column, because there is no position to buy. Canon § 10 makes a mention exposure
 * rather than ranking, and drawing it with the board's furniture would be the design quietly
 * suggesting that a dollar buys a place in the standings.
 *
 * Three facts a founder can otherwise get wrong are **stated**, never implied:
 *
 *   1. it sits outside the Top 10 and never becomes a Top 10 position by being bought;
 *   2. it runs for one display day — tomorrow's, in UTC (canon § 8);
 *   3. it never rolls over; when that day ends the listing is finished.
 *
 * The price is core's `priceCents`, not a constant here: canon § 10 owns the rate card, and
 * a second copy in a UI would eventually disagree about what a founder is charged. The
 * sponsored sentence is core's `disclosure.message` verbatim (canon § 12).
 *
 * Every eligibility decision belongs to core. This panel says what it already knows — that a
 * deal is bidding, or already listed — so a founder is not sent to press a button that
 * cannot work, but nothing here decides that a purchase is allowed. Core re-checks all of
 * it, and its refusal is the answer that counts.
 *
 * **The rows come from the deals read, not from the auction read.** Canon § 10 ties a
 * mention to a display *day*, and core accepts one for a day whose auction row does not
 * exist yet — but `GET /v1/seller/auction/tomorrow` answers `deals: []` in exactly that
 * state, because it lists who may *bid*. Building these rows from it would tell a founder
 * that no deal of theirs can be listed on precisely the day core would happily list one.
 * `GET /v1/seller/deals` filtered to `approved` is the same gate canon § 10 names.
 */

export function MentionsSkeleton() {
  return (
    <Card className={styles.panel}>
      <Skeleton width="10rem" height="2rem" />
      <Skeleton width="70%" height="1rem" />
      <Skeleton width="100%" height="3rem" />
    </Card>
  );
}

export interface MentionsPanelProps {
  offer: HonorableMentionOffer;
  /** The day these listings run. Core's date, never computed here (canon § 8). */
  displayDate: string;
  /**
   * The seller's approved deals, or `null` while the deals read has not landed. Canon § 10
   * puts a mention behind the same approval gate as every other placement, so this is the
   * whole set that may be listed.
   */
  approvedDeals: SellerDeal[] | null;
  /**
   * The deals holding a standing bid for the same day, from the auction read. Canon § 7
   * gives a deal one position a day, so these are told what they hold rather than offered a
   * second one.
   */
  biddingDealIds: readonly string[];
  purchaseMention: (input: { dealId: string }) => Promise<PurchasedMentionResult>;
  /** Called after core accepts a purchase, so the desk can re-read the day and the balance. */
  onMentionPurchased: () => void;
}

export function MentionsPanel({
  offer,
  displayDate,
  approvedDeals,
  biddingDealIds,
  purchaseMention,
  onMentionPurchased,
}: MentionsPanelProps) {
  const held = new Set(offer.purchased.map((mention) => mention.dealId));
  const bidding = new Set(biddingDealIds);
  const day = formatDisplayDateLong(displayDate);

  return (
    <div className={styles.panel}>
      <Card tone="lead" className={styles.terms}>
        <p className={styles.price}>
          <span className={`${styles.amount} figure`}>{formatMoney(offer.priceCents)}</span>
          <span className={styles.per}>for one display day</span>
        </p>
        <p className={styles.what}>
          An Honorable Mention lists your deal on {day}, outside the Top 10. It is a separate
          section of the board, not a position in the standings — buying one never moves a
          deal into the Top 10.
        </p>
        {/*
         * Canon § 10's expiry, stated rather than implied. "One display day" and "never
         * rolls over" are the two things a founder is most likely to assume otherwise,
         * having just read that an unfilled Top 10 rolls previous deals forward.
         */}
        <p className={styles.expiry}>
          It runs for that one day and then expires. Honorable Mentions never roll over to
          the next day, so a listing on {day} does nothing for any day after it.
        </p>
        <p className={styles.disclosure}>{offer.disclosure.message}</p>
      </Card>

      {offer.purchased.length === 0 ? null : (
        <ul className={styles.held} role="list">
          {offer.purchased.map((mention) => (
            <li key={mention.placementId} className={styles.heldItem}>
              <p className={styles.heldTitle}>
                {mention.productName} is listed for {day}
              </p>
              <p className={styles.heldNote}>
                Bought {formatInstantDate(mention.purchasedAt) ?? mention.purchasedAt} ·
                expires when that day ends · does not roll over.
              </p>
            </li>
          ))}
        </ul>
      )}

      {approvedDeals === null ? (
        // The rate card, the disclosure and anything already held come from the auction
        // read and are on screen; only the buyable rows wait on the deals read. Saying so
        // is more honest than an empty state that would read as "you have no deals".
        <p className={styles.pending}>Loading the deals you can list…</p>
      ) : approvedDeals.length === 0 ? (
        <Callout title="No deal of yours can be listed yet">
          Only an approved deal can take an Honorable Mention, the same gate every other
          placement passes. Where each of your deals stands in review is on the panel below.
        </Callout>
      ) : (
        <ul className={styles.deals} role="list">
          {approvedDeals.map((deal) => (
            <li key={deal.id}>
              <MentionRow
                deal={deal}
                displayDate={displayDate}
                priceCents={offer.priceCents}
                alreadyListed={held.has(deal.id)}
                isBidding={bidding.has(deal.id)}
                purchaseMention={purchaseMention}
                onMentionPurchased={onMentionPurchased}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type PurchaseStatus = "idle" | "submitting" | "success" | "error";

/**
 * One approved deal and its buy control.
 *
 * A deal that already holds a listing, or that is bidding for the same day, gets the reason
 * in place of a button. That is not a second copy of canon § 7's one-position-a-day rule —
 * core enforces it and would refuse either purchase — it is the same reason worded for the
 * founder in advance, so the alternative they are being offered is an honest one.
 */
function MentionRow({
  deal,
  displayDate,
  priceCents,
  alreadyListed,
  isBidding,
  purchaseMention,
  onMentionPurchased,
}: {
  deal: SellerDeal;
  displayDate: string;
  priceCents: number;
  alreadyListed: boolean;
  isBidding: boolean;
  purchaseMention: MentionsPanelProps["purchaseMention"];
  onMentionPurchased: () => void;
}) {
  const [status, setStatus] = useState<PurchaseStatus>("idle");
  const [failure, setFailure] = useState<string | null>(null);
  const [bought, setBought] = useState<PurchasedMentionResult | null>(null);
  const inFlight = useRef(false);

  const buy = useCallback(() => {
    // A second press while one purchase is in flight is ignored. Canon § 10 gives a deal one
    // mention for one day, and a founder pressing twice must not be charged twice — core
    // refuses the duplicate, but the honest behaviour is not to send it.
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus("submitting");
    setFailure(null);
    setBought(null);

    void purchaseMention({ dealId: deal.id })
      .then((result) => {
        setBought(result);
        setStatus("success");
        onMentionPurchased();
      })
      .catch((caught: unknown) => {
        const error =
          caught instanceof ApiError
            ? caught
            : new ApiError("Dealbid couldn't buy that listing.", { status: null });
        setFailure(messageForMentionError(error));
        setStatus("error");
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [deal.id, purchaseMention, onMentionPurchased]);

  return (
    <Card as="article" className={styles.deal}>
      <div className={styles.head}>
        <h3 className={styles.product}>{deal.productName}</h3>
        {alreadyListed || isBidding ? null : (
          <Button
            variant="secondary"
            onClick={buy}
            disabled={status === "submitting"}
            className={styles.buy}
          >
            {status === "submitting"
              ? "Buying…"
              : `List for ${formatMoney(priceCents)}`}
            <VisuallyHidden>
              {" "}
              — Honorable Mention for {deal.productName} on{" "}
              {formatDisplayDateLong(displayDate)}
            </VisuallyHidden>
          </Button>
        )}
      </div>

      {alreadyListed ? (
        <p className={styles.reason}>
          Already listed for {formatDisplayDateLong(displayDate)}. A deal takes one position
          a day.
        </p>
      ) : isBidding ? (
        <p className={styles.reason}>
          Bidding for tomorrow&rsquo;s Top 10. A deal takes one position a day, so this one
          can&rsquo;t also take an Honorable Mention on {formatDisplayDateLong(displayDate)}.
        </p>
      ) : (
        <p className={styles.offer}>
          <span className="figure">{formatMoney(priceCents)}</span> of promotional credit,
          spent when the listing is bought. It runs for the whole of{" "}
          {formatDisplayDateLong(displayDate)} and then expires.
        </p>
      )}

      {status === "success" && bought !== null ? (
        <MentionBought result={bought} productName={deal.productName} />
      ) : failure === null ? null : (
        <p className={styles.failure} role="alert">
          <VisuallyHidden>Error: </VisuallyHidden>
          {failure}
        </p>
      )}
    </Card>
  );
}

/**
 * The confirmation, in a polite live region.
 *
 * It says four things, each one a founder can otherwise get wrong: which day was bought,
 * that it is outside the Top 10, that it expires with that day and does not roll over, and
 * what the balance did. The price and the date are quoted from core's response rather than
 * from what this app sent, so the confirmation states what actually happened.
 */
function MentionBought({
  result,
  productName,
}: {
  result: PurchasedMentionResult;
  productName: string;
}) {
  const day = formatDisplayDateLong(result.mention.displayDate);

  return (
    <div className={styles.bought} role="status">
      <p className={styles.boughtTitle}>
        Listed — {productName} has an Honorable Mention on {day} for{" "}
        <span className="figure">{formatMoney(result.mention.priceCents)}</span>.
      </p>
      <p>
        It appears outside the Top 10, in the Honorable Mentions section of that day&rsquo;s
        board. It is not a Top 10 position and does not become one.
      </p>
      <p>
        The listing ends when {day} does. It does not roll over, so list again if you want
        another day.
      </p>
      <p className={styles.boughtCredit}>
        <span className="figure">{formatMoney(result.credit.availableCents)}</span> available
        · <span className="figure">{formatMoney(result.credit.reservedCents)}</span> reserved.
        Credit for a mention is held until the day it runs, and is not returned.
      </p>
    </div>
  );
}
